import { useState, useEffect, useCallback, useRef, memo } from "react";
import axios from "axios";
import { 
  Factory, 
  CheckCircle2, 
  XCircle, 
  Clock,
  Pause,
  Play,
  Wrench,
  Settings,
  RefreshCw,
  CloudSun,
  Droplets,
  Wind,
  Thermometer,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROTATING_LOGOS = [
  "/logos/epconstrux.jpg",
  "/logos/ep3d-project.png",
  "/logos/decorclick.jpeg",
  "/logos/eplastik.png",
];

const VIDEOS = [
  `${process.env.REACT_APP_BACKEND_URL}/api/videos/anuncio-eng3d.mp4`,
  `${process.env.REACT_APP_BACKEND_URL}/api/videos/apresentacao-logo.mp4`,
  `${process.env.REACT_APP_BACKEND_URL}/api/videos/video-logotipo.mp4`,
];

const STATUS_CONFIG = {
  running: { label: "EM EXECUCAO", color: "bg-green-500", borderColor: "border-green-500", icon: Play },
  idle: { label: "PARADA", color: "bg-amber-500", borderColor: "border-amber-500", icon: Pause },
  stopped: { label: "DESLIGADA", color: "bg-red-500", borderColor: "border-red-500", icon: XCircle },
  maintenance: { label: "MANUTENCAO", color: "bg-purple-500", borderColor: "border-purple-500", icon: Wrench },
  setup: { label: "SETUP", color: "bg-blue-500", borderColor: "border-blue-500", icon: Settings },
};

function MachineCard({ data }) {
  const { machine, active_order, production } = data;
  const statusConfig = STATUS_CONFIG[machine.status] || STATUS_CONFIG.idle;
  const StatusIcon = statusConfig.icon;
  const progress = active_order ? Math.min(100, (production.good_quantity / active_order.good_quantity_target) * 100) : 0;

  return (
    <div className={`bg-white rounded-xl shadow-lg border-2 ${statusConfig.borderColor} overflow-hidden`} data-testid={`sf3-machine-${machine.code}`}>
      <div className={`${statusConfig.color} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <StatusIcon className="h-5 w-5 text-white" />
          <span className="text-white font-bold text-lg">{machine.code}</span>
        </div>
        <span className="text-white font-bold text-sm uppercase">{statusConfig.label}</span>
      </div>
      <div className="p-4">
        <div className="text-center mb-2">
          <p className="text-gray-700 font-medium">{machine.description || "-"}</p>
          <p className="text-gray-400 text-sm">{machine.tonnage}T</p>
        </div>
        {active_order ? (
          <>
            <div className="border-t pt-3 mt-3">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-500">Ordem:</span>
                <span className="font-bold">{active_order.code}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-500">Peca:</span>
                <span className="font-medium">{active_order.part_name || "-"}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-500">Progresso:</span>
                <span className="font-bold">{production.good_quantity}/{active_order.good_quantity_target}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className={`h-3 rounded-full ${progress >= 90 ? 'bg-green-500' : progress >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`} style={{ width: `${Math.min(progress, 100)}%` }}></div>
              </div>
              <p className="text-center text-sm font-bold mt-1 text-gray-600">{Math.round(progress)}%</p>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <div className="text-center bg-green-50 rounded p-2">
                <p className="text-xs text-green-600">Boas</p>
                <p className="font-bold text-green-700">{production.good_quantity}</p>
              </div>
              <div className="text-center bg-red-50 rounded p-2">
                <p className="text-xs text-red-600">Rejeitadas</p>
                <p className="font-bold text-red-700">{production.rejected_quantity}</p>
              </div>
              <div className="text-center bg-blue-50 rounded p-2">
                <p className="text-xs text-blue-600">Ciclos</p>
                <p className="font-bold text-blue-700">{production.total_cycles}</p>
              </div>
            </div>
          </>
        ) : (
          <div className="py-6 text-center">
            <Factory className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 font-medium">Sem ordem ativa</p>
            <p className="text-gray-400 text-sm">Aguardando atribuicao</p>
          </div>
        )}
      </div>
    </div>
  );
}

function WeatherWidget() {
  const [weather, setWeather] = useState(null);
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const r = await fetch("https://api.open-meteo.com/v1/forecast?latitude=39.24&longitude=-8.69&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&daily=temperature_2m_max,temperature_2m_min&timezone=Europe/Lisbon&forecast_days=1");
        setWeather(await r.json());
      } catch (e) {}
    };
    fetchWeather();
    const iv = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);
  if (!weather?.current) return null;
  return (
    <div className="flex items-center gap-3 text-white text-sm">
      <CloudSun className="h-5 w-5 text-amber-300" />
      <span className="font-bold text-base">{weather.current.temperature_2m}C</span>
      <span className="text-amber-200">Santarem</span>
      <span className="text-slate-400">|</span>
      <span className="text-slate-300 flex items-center gap-1"><Droplets className="h-3.5 w-3.5" />{weather.current.relative_humidity_2m}%</span>
      <span className="text-slate-300 flex items-center gap-1"><Wind className="h-3.5 w-3.5" />{weather.current.wind_speed_10m}km/h</span>
      {weather.daily && <span className="text-slate-400 flex items-center gap-1"><Thermometer className="h-3.5 w-3.5" />{weather.daily.temperature_2m_min[0]}/{weather.daily.temperature_2m_max[0]}</span>}
    </div>
  );
}

function NextOrdersTable() {
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    const fetchOrders = async () => {
      try { const r = await axios.get(`${API}/shop-floor/next-orders`); setOrders(r.data); } catch (e) {}
    };
    fetchOrders();
    const iv = setInterval(fetchOrders, 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  const statusColors = { planned: "bg-blue-500", pending: "bg-amber-500", ready: "bg-green-500" };
  const statusLabels = { planned: "PLANEADA", pending: "PENDENTE", ready: "PRONTA" };

  return (
    <div className="bg-slate-900 rounded-tl-xl overflow-hidden shadow-2xl h-full flex flex-col">
      <div className="bg-amber-500 px-4 py-2 flex items-center justify-between shrink-0">
        <h3 className="text-white font-bold text-sm uppercase tracking-wider flex items-center gap-2">
          <Clock className="h-4 w-4" /> Proximas Ordens de Producao
        </h3>
        <span className="text-amber-100 text-xs">{orders.length} ordens</span>
      </div>
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-800 text-slate-400 text-xs uppercase tracking-wider">
              <th className="text-left px-3 py-2 font-semibold">Estado</th>
              <th className="text-left px-3 py-2 font-semibold">Ordem</th>
              <th className="text-left px-3 py-2 font-semibold">Descricao</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, i) => (
              <tr key={i} className="border-b border-slate-800 hover:bg-slate-800/50">
                <td className="px-3 py-2">
                  <span className={`${statusColors[order.status] || "bg-gray-500"} text-white text-xs px-2 py-0.5 rounded font-bold`}>
                    {statusLabels[order.status] || order.status?.toUpperCase()}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-200 font-mono font-bold">{order.code}</td>
                <td className="px-3 py-2 text-slate-300">{order.part_description || order.part_name || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const VideoPlayer = memo(function VideoPlayer() {
  const videoRef = useRef(null);
  const idxRef = useRef(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const playNext = () => {
      idxRef.current = (idxRef.current + 1) % VIDEOS.length;
      video.src = VIDEOS[idxRef.current];
      video.load();
      video.play().catch(() => {});
    };

    video.addEventListener("ended", playNext);
    video.src = VIDEOS[0];
    video.load();
    video.play().catch(() => {});

    return () => video.removeEventListener("ended", playNext);
  }, []);

  return (
    <video
      ref={videoRef}
      className="absolute inset-0 w-full h-full object-contain"
      muted
      playsInline
      preload="auto"
    />
  );
});

export default function ShopFloor3() {
  const [dashboardData, setDashboardData] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentLogoIdx, setCurrentLogoIdx] = useState(0);
  const [currentMachineIdx, setCurrentMachineIdx] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("mes_token");
      const r = await axios.get(`${API}/dashboard/factory`, { headers: { Authorization: `Bearer ${token}` } });
      setDashboardData(r.data);
    } catch (e) {}
  }, []);

  useEffect(() => { fetchData(); const iv = setInterval(fetchData, 30000); return () => clearInterval(iv); }, [fetchData]);
  useEffect(() => { const iv = setInterval(() => setCurrentTime(new Date()), 1000); return () => clearInterval(iv); }, []);

  // Rotate logos every 30s
  useEffect(() => {
    const iv = setInterval(() => { setCurrentLogoIdx(prev => (prev + 1) % ROTATING_LOGOS.length); }, 30000);
    return () => clearInterval(iv);
  }, []);

  // Rotate machines every 30s
  useEffect(() => {
    const machineCount = dashboardData?.machines?.length || 1;
    const iv = setInterval(() => { setCurrentMachineIdx(prev => (prev + 1) % machineCount); }, 30000);
    return () => clearInterval(iv);
  }, [dashboardData?.machines?.length]);

  const machines = dashboardData?.machines || [];
  const totalMachines = machines.length;
  const runningMachines = machines.filter(m => m.machine.status === "running").length;
  const totalGoodProduction = machines.reduce((sum, m) => sum + (m.production?.good_quantity || 0), 0);
  const totalRejected = machines.reduce((sum, m) => sum + (m.production?.rejected_quantity || 0), 0);
  const qualityRate = (totalGoodProduction + totalRejected) > 0 ? Math.round((totalGoodProduction / (totalGoodProduction + totalRejected)) * 100) : 100;

  const currentMachine = machines[currentMachineIdx % Math.max(machines.length, 1)];

  return (
    <div className="h-screen flex flex-col bg-slate-100 overflow-hidden" data-testid="shop-floor-3">
      {/* Header */}
      <header className="shrink-0 bg-white shadow-md px-6 py-3 flex items-center justify-between border-b-4 border-amber-500">
        <div className="flex items-center gap-4">
          <img src="/ep3d-logo.png" alt="EP3D" className="h-12" />
          <div>
            <h1 className="font-bold text-2xl text-gray-800">Painel da Fabrica 3</h1>
            <p className="text-gray-500 text-sm">Monitorizacao em Tempo Real</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-center px-4 py-2 bg-green-50 rounded-lg border border-green-200">
            <p className="text-xs text-green-600 uppercase font-medium">Maquinas Ativas</p>
            <p className="text-3xl font-bold text-green-700">{runningMachines}/{totalMachines}</p>
          </div>
          <div className="text-center px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-600 uppercase font-medium">Producao Total</p>
            <p className="text-3xl font-bold text-blue-700">{totalGoodProduction.toLocaleString()}</p>
          </div>
          <div className="text-center px-4 py-2 bg-red-50 rounded-lg border border-red-200">
            <p className="text-xs text-red-600 uppercase font-medium">Rejeitadas</p>
            <p className="text-3xl font-bold text-red-700">{totalRejected.toLocaleString()}</p>
          </div>
          <div className="text-center px-4 py-2 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-xs text-purple-600 uppercase font-medium">Taxa Qualidade</p>
            <p className="text-3xl font-bold text-purple-700">{qualityRate}%</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-mono text-4xl font-bold text-gray-800">{currentTime.toLocaleTimeString("pt-PT")}</p>
          <p className="text-gray-500 text-sm">{currentTime.toLocaleDateString("pt-PT", { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        </div>
      </header>

      {/* Main Content - 2 columns */}
      <main className="flex-1 flex overflow-hidden">
        {/* Left Column - Logo + Machine Card + Orders */}
        <div className="w-[320px] flex flex-col shrink-0 border-r border-gray-200">
          {/* Rotating Logo */}
          <div className="bg-white p-4 flex items-center justify-center shrink-0" style={{ height: "140px" }}>
            <img 
              src={ROTATING_LOGOS[currentLogoIdx]} 
              alt="Logo" 
              className="max-h-[120px] max-w-[280px] object-contain transition-opacity duration-1000"
              key={currentLogoIdx}
            />
          </div>

          {/* Rotating Machine Card */}
          <div className="px-3 py-2 shrink-0">
            {currentMachine ? (
              <MachineCard data={currentMachine} />
            ) : (
              <div className="bg-white rounded-xl p-8 text-center border">
                <Factory className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p className="text-gray-400">A carregar...</p>
              </div>
            )}
            <p className="text-center text-xs text-gray-400 mt-1">
              Maquina {(currentMachineIdx % Math.max(machines.length, 1)) + 1} de {machines.length} (troca a cada 30s)
            </p>
          </div>

          {/* Orders Table - fills remaining space */}
          <div className="flex-1 overflow-hidden">
            <NextOrdersTable />
          </div>
        </div>

        {/* Right Column - Video Player */}
        <div className="flex-1 flex flex-col">
          <div className="bg-amber-500 px-4 py-2 flex items-center justify-between shrink-0">
            <span className="text-white font-bold text-sm uppercase tracking-wider flex items-center gap-2">
              <span className="h-2 w-2 bg-white rounded-full animate-pulse" />
              Videos Institucionais
            </span>
          </div>
          <div className="flex-1 bg-black relative">
            <VideoPlayer />
          </div>
        </div>
      </main>

      {/* Bottom Bar */}
      <div className="shrink-0 bg-slate-900 flex items-center justify-between px-6 py-2">
        <WeatherWidget />
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <RefreshCw className="h-3 w-3" />
          <span>Auto-refresh 30s</span>
          <span className="text-slate-700 mx-2">|</span>
          <span>EP3D ENGINEERING PROJECT</span>
        </div>
      </div>
    </div>
  );
}
